// Jedno pole pro hledání a asistenta v hlavičce (krok 12.6 + rozhodnutí 05/1520 z 2. 10. 2026, postaveno W24;
// prototyp data-screen-label="Vyhledávání", ř. 326–345): pole „Hledat nebo se zeptat…“, pod ním panel s výsledky
// po skupinách (zakázky, zákazníci a provozovny, vydané doklady, vážní lístky, smlouvy, vozidla, odpady z katalogu,
// zaměstnanci, sekce aplikace; skupiny kreslí podle toho, co server vrátil), jako druhé kolo dokumenty
// podle smyslu (archiv, smlouvy, přílohy — i skeny) a úplně dole řádek „Zeptat se asistenta: …“.
//
//   * hledá server (bez diakritiky, jen to, co účet smí vidět); klient odloží dotaz o 250 ms po posledním znaku
//     a starý požadavek zruší (react-query přeruší `signal` dotazu, který nahradil nový text). Rychlé výsledky
//     počítá kód bez modelu — psaní nic nestojí; asistent (model) se zavolá až výslovnou volbou řádku „Zeptat se“;
//   * klávesnice: ↓/↑ vybírá řádek (i „Zeptat se asistenta“ a „Pokračovat v poslední konverzaci“), Enter otevře
//     zvýrazněný řádek, jinak první výsledek a když žádný není, zeptá se asistenta; Ctrl+Enter se zeptá asistenta vždy
//     (v nové konverzaci), Ctrl+Shift+Enter vždy v poslední konverzaci; Esc zavře panel;
//   * zkratka „/“ (mimo textová pole) i Ctrl+K (odkudkoli, i z jiného pole) skočí do téhož pole; Ctrl+K při
//     otevřeném asistentovi ho zavře a vrátí se do pole;
//   * dotaz jde asistentovi jako nová konverzace (`otevritAsistenta({ dotaz, nova })`); pod řádkem „Zeptat se“ je
//     viditelná volba „Pokračovat v poslední konverzaci“ (`otevritAsistenta({ dotaz, pokracovat })`) — rozhodnutí
//     zadavatele k 05/2202 z 5. 10. 2026: výchozí je nová konverzace, pokračovat jde vždy; panel se stahuje až při
//     prvním otevření (`asistent/AsistentVHlavicce.tsx`) — do hlavního balíku se nedostane;
//   * účet bez práva na asistenta řádek „Zeptat se“ nevidí (jen hledání); účet bez hledání vidí jen asistenta;
//   * diktát (tlačítko z W13) je v panelu pod polem a vloží text do téhož pole, takže jde nadiktovat hledání
//     i otázku; v panelu asistenta zůstává vlastní;
//   * pod 950 px se pole do hlavičky nevejde — je tam tlačítko s lupou a hledání se otevře přes horní okraj
//     obrazovky (telefon i tablet na výšku) s vlastním polem; asistent žádné další tlačítko nemá, takže nic
//     nepřekrývá; řádky mají cíl ≥ 44 px.
import type { Ja } from '@ecoser/shared';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router';
import { naChybu } from '../api/klient';
import {
  AKCE_HLEDANI,
  type HledaniPolozka,
  type HledaniSkupina,
  lzeHledat,
  useHledani,
} from '../api/hledani';
import { otevritAsistenta, usePanelAsistenta, zavritAsistenta } from '../asistent/panel-stav';
import { prikladyDotazu, smiAsistenta } from '../asistent/pravo-asistenta';
import { ZnackaAsistenta } from '../asistent/ZnackaAsistenta';
import { lina, NacitaniCasti } from '../komponenty/NacitaniCasti';
import { maAkci } from '../komponenty/prava';
import { Ikona } from './ikony';
import { DOTAZ_VYSOUVACI_MENU, useMediaDotaz } from './telefon';
import './hledani.css';

// Diktát se stahuje až s polem v panelu (mikrofon, přepis, offline — nic z toho do hlavního balíku nepatří).
const TlacitkoDiktatu = lina(() =>
  import('../komponenty/TlacitkoDiktatu').then((m) => m.TlacitkoDiktatu),
);

/** Odklad dotazu po posledním znaku (ms) — testy mohou dát 0. */
export const ODKLAD_HLEDANI_MS = 250;
/** Klávesová zkratka pole hledání (vedle ní vede do pole i Ctrl+K). */
export const ZKRATKA_HLEDANI = '/';
/** Kolik příkladů dotazů ukáže prázdné pole. */
const POCET_PRIKLADU = 3;

/** Je fokus v poli, kam se píše? Tam „/“ patří do textu, ne hledání. */
function piseSeDoPole(cil: EventTarget | null): boolean {
  if (!(cil instanceof HTMLElement)) return false;
  const tag = cil.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || cil.isContentEditable;
}

/**
 * Jedna volba v panelu, po které jde šipkami: výsledek hledání, dotaz asistentovi (novou konverzací), dotaz v poslední
 * konverzaci, příklad, otevření asistenta.
 */
type Volba =
  | { druh: 'vysledek'; klic: string; polozka: HledaniPolozka }
  | { druh: 'zeptat'; klic: string; dotaz: string; priklad: boolean }
  | { druh: 'pokracovat'; klic: string; dotaz: string }
  | { druh: 'otevrit'; klic: string };

interface Props {
  ja: Ja;
  odkladMs?: number;
}

export function HledaniVHlavicce({ ja, odkladMs = ODKLAD_HLEDANI_MS }: Props) {
  const hledaniPovoleno = maAkci(ja.akce, AKCE_HLEDANI);
  const asistent = smiAsistenta(ja);
  const povoleno = hledaniPovoleno || asistent;
  const uzke = useMediaDotaz(DOTAZ_VYSOUVACI_MENU);
  const navigate = useNavigate();
  const zaklad = useId();
  const idPanelu = `${zaklad}-vysledky`;
  const idPolozky = (i: number) => `${zaklad}-polozka-${i}`;
  const koren = useRef<HTMLDivElement>(null);
  const pole = useRef<HTMLInputElement>(null);
  const tlacitko = useRef<HTMLButtonElement>(null);
  /** Programové vrácení fokusu do pole (po zavření asistenta) nemá znovu otevřít panel. */
  const potlacitOtevreni = useRef(false);
  const { otevreny: asistentOtevreny } = usePanelAsistenta();

  const [text, setText] = useState('');
  const [odlozeny, setOdlozeny] = useState('');
  const [otevreno, setOtevreno] = useState(false);
  const [zvyraznena, setZvyraznena] = useState(-1);
  /** Enter přišel dřív, než server vrátil výsledky: rozhodne se, až budou. */
  const [cekaNaEnter, setCekaNaEnter] = useState(false);

  useEffect(() => {
    if (odkladMs <= 0) {
      setOdlozeny(text);
      return undefined;
    }
    const casovac = window.setTimeout(() => setOdlozeny(text), odkladMs);
    return () => window.clearTimeout(casovac);
  }, [text, odkladMs]);

  const hledat = hledaniPovoleno && otevreno && lzeHledat(odlozeny);
  const zaznamy = useHledani(odlozeny, 'zaznamy', hledat);
  const dokumenty = useHledani(odlozeny, 'dokumenty', hledat);

  const skupiny: HledaniSkupina[] = useMemo(() => {
    if (!hledat) return [];
    return [...(zaznamy.data?.skupiny ?? []), ...(dokumenty.data?.skupiny ?? [])].filter(
      (s) => s.polozky.length > 0,
    );
  }, [hledat, zaznamy.data, dokumenty.data]);
  const polozky: HledaniPolozka[] = useMemo(() => skupiny.flatMap((s) => s.polozky), [skupiny]);

  const dotaz = text.trim();
  const mozneHledat = lzeHledat(text);
  const priklady = useMemo(() => prikladyDotazu(ja).slice(0, POCET_PRIKLADU), [ja]);

  // Volby panelu v pořadí na obrazovce: výsledky, pak (dole) asistent — dotaz, nebo u prázdného pole příklady.
  const volby: Volba[] = useMemo(() => {
    const v: Volba[] = polozky.map((polozka) => ({
      druh: 'vysledek',
      klic: `${polozka.typ}-${polozka.id}`,
      polozka,
    }));
    if (!asistent) return v;
    if (mozneHledat) {
      v.push({ druh: 'zeptat', klic: 'zeptat', dotaz, priklad: false });
      v.push({ druh: 'pokracovat', klic: 'pokracovat', dotaz });
    } else {
      for (const priklad of priklady) {
        v.push({ druh: 'zeptat', klic: `priklad-${priklad}`, dotaz: priklad, priklad: true });
      }
      v.push({ druh: 'otevrit', klic: 'otevrit' });
    }
    return v;
  }, [polozky, asistent, mozneHledat, dotaz, priklady]);

  useEffect(() => setZvyraznena(-1), [odlozeny]);

  const zavrit = useCallback(() => {
    setOtevreno(false);
    setZvyraznena(-1);
    setCekaNaEnter(false);
  }, []);

  const vycistit = () => {
    zavrit();
    setText('');
    setOdlozeny('');
  };

  const otevritPolozku = (p: HledaniPolozka) => {
    vycistit();
    navigate(p.odkaz);
  };

  /**
   * Dotaz z pole je nová otázka asistentovi: pole se vyčistí a panel asistenta ji odešle jako novou konverzaci
   * (rozhodnutí zadavatele k 05/2202 z 5. 10. 2026; pokračovat v poslední konverzaci jde volbou pod tím).
   */
  const zeptatSe = (otazka: string) => {
    vycistit();
    otevritAsistenta({ dotaz: otazka, nova: true });
  };

  /** Totéž, ale dotaz se připojí k poslední konverzaci (otevřené v panelu, jinak nejnovější z Dotazů). */
  const pokracovatVKonverzaci = (otazka: string) => {
    vycistit();
    otevritAsistenta({ dotaz: otazka, pokracovat: true });
  };

  const provest = (volba: Volba) => {
    if (volba.druh === 'vysledek') otevritPolozku(volba.polozka);
    else if (volba.druh === 'zeptat') zeptatSe(volba.dotaz);
    else if (volba.druh === 'pokracovat') pokracovatVKonverzaci(volba.dotaz);
    else {
      vycistit();
      otevritAsistenta();
    }
  };

  // Server má výsledky prvního kola pro právě napsaný text (dokumenty podle smyslu se nečekají — model je pomalejší).
  const ustaleno =
    !hledaniPovoleno ||
    !mozneHledat ||
    (dotaz === odlozeny.trim() &&
      !zaznamy.isFetching &&
      !zaznamy.isPlaceholderData &&
      (zaznamy.isSuccess || zaznamy.isError));

  /** Enter bez zvýrazněného řádku: první výsledek, a když žádný není, otázka asistentovi. */
  const rozhodnoutEnter = () => {
    const prvni = volby.find((v) => v.druh === 'vysledek');
    if (prvni) provest(prvni);
    else if (asistent && mozneHledat) zeptatSe(dotaz);
  };

  useEffect(() => {
    if (!cekaNaEnter || !ustaleno) return;
    setCekaNaEnter(false);
    rozhodnoutEnter();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rozhoduje se jednou, ve chvíli, kdy výsledky přijdou
  }, [cekaNaEnter, ustaleno]);

  // Zkratky: „/“ mimo textové pole a Ctrl+K odkudkoli skočí do pole (na úzké obrazovce se hledání otevře).
  useEffect(() => {
    if (!povoleno) return undefined;
    const naKlavesu = (udalost: globalThis.KeyboardEvent) => {
      const ctrlK =
        (udalost.ctrlKey || udalost.metaKey) &&
        !udalost.altKey &&
        udalost.key.toLowerCase() === 'k';
      const lomitko =
        udalost.key === ZKRATKA_HLEDANI &&
        !udalost.ctrlKey &&
        !udalost.metaKey &&
        !udalost.altKey &&
        !piseSeDoPole(udalost.target) &&
        !asistentOtevreny;
      if (!ctrlK && !lomitko) return;
      udalost.preventDefault();
      if (ctrlK) zavritAsistenta();
      setOtevreno(true);
      window.setTimeout(() => {
        pole.current?.focus();
        if (ctrlK) pole.current?.select();
      }, 0);
    };
    document.addEventListener('keydown', naKlavesu);
    return () => document.removeEventListener('keydown', naKlavesu);
  }, [povoleno, asistentOtevreny]);

  // Překryv na úzké obrazovce: po otevření fokus do pole, po zavření zpět na tlačítko s lupou.
  const bylOtevreny = useRef(false);
  useEffect(() => {
    if (uzke && otevreno) pole.current?.focus();
    if (uzke && bylOtevreny.current && !otevreno) tlacitko.current?.focus();
    bylOtevreny.current = otevreno;
  }, [uzke, otevreno]);

  // Po zavření panelu asistenta se fokus vrátí tam, odkud se ptalo (do pole, na úzké obrazovce na lupu).
  const bylAsistent = useRef(false);
  useEffect(() => {
    if (bylAsistent.current && !asistentOtevreny) {
      if (uzke) {
        tlacitko.current?.focus();
      } else {
        potlacitOtevreni.current = true;
        pole.current?.focus();
        window.setTimeout(() => {
          potlacitOtevreni.current = false;
        }, 0);
      }
    }
    bylAsistent.current = asistentOtevreny;
  }, [asistentOtevreny, uzke]);

  if (!povoleno) return null;

  const naKlavesuPole = (udalost: KeyboardEvent<HTMLInputElement>) => {
    if (udalost.key === 'Escape') {
      udalost.preventDefault();
      // První Esc zavře výsledky, druhý (na šířce pole) opustí pole.
      if (otevreno) zavrit();
      else pole.current?.blur();
      return;
    }
    if (udalost.key === 'ArrowDown') {
      udalost.preventDefault();
      setOtevreno(true);
      if (volby.length > 0) setZvyraznena((i) => (i + 1) % volby.length);
      return;
    }
    if (udalost.key === 'ArrowUp') {
      udalost.preventDefault();
      // ↑ z nezvýrazněného řádku skočí na „Zeptat se asistenta“ (výchozí volba — nová konverzace), z prvního řádku na poslední.
      if (volby.length > 0) {
        const zeptat = volby.findIndex((v) => v.druh === 'zeptat' && !v.priklad);
        setZvyraznena((i) => (i < 0 && zeptat >= 0 ? zeptat : i <= 0 ? volby.length - 1 : i - 1));
      }
      return;
    }
    if (udalost.key === 'Enter' && !udalost.nativeEvent.isComposing) {
      const vybrana = zvyraznena >= 0 ? volby[zvyraznena] : undefined;
      if (vybrana) {
        udalost.preventDefault();
        provest(vybrana);
      } else if (udalost.ctrlKey || udalost.metaKey) {
        udalost.preventDefault();
        if (asistent && mozneHledat) {
          if (udalost.shiftKey) pokracovatVKonverzaci(dotaz);
          else zeptatSe(dotaz);
        }
      } else if (mozneHledat) {
        udalost.preventDefault();
        // Po Esc je panel zavřený a hledání vypnuté — Enter ho znovu rozjede, aby měl na co čekat.
        setOtevreno(true);
        if (ustaleno) rozhodnoutEnter();
        else setCekaNaEnter(true);
      }
    }
  };

  const nacitaZaznamy = hledat && zaznamy.isFetching;
  const nacitaDokumenty = hledat && dokumenty.isFetching;
  const chyba = zaznamy.isError ? naChybu(zaznamy.error) : null;
  const ukazatPanel = otevreno && (uzke || mozneHledat || asistent);
  const prazdno = hledat && !nacitaZaznamy && !nacitaDokumenty && !chyba && polozky.length === 0;
  const stavDokumentu = dokumenty.data?.dokumenty;
  const diktat = asistent && ja.diktat?.dostupny === true;

  const mouseDownBezFokusu = (e: React.MouseEvent) => e.preventDefault();
  const radekAsistenta = (volba: Volba, i: number) => {
    const aktivni = i === zvyraznena;
    const trida = `hledani__polozka hledani__polozka--asistent${aktivni ? ' hledani__polozka--aktivni' : ''}`;
    if (volba.druh === 'otevrit') {
      return (
        <div
          key={volba.klic}
          id={idPolozky(i)}
          role="option"
          aria-selected={aktivni}
          className={trida}
          data-testid="hledani-otevrit-asistenta"
          onMouseDown={mouseDownBezFokusu}
          onMouseEnter={() => setZvyraznena(i)}
          onClick={() => provest(volba)}
        >
          <span className="hledani__znacka" aria-hidden="true">
            <ZnackaAsistenta velikost={16} />
          </span>
          <div className="hledani__text">
            <div className="hledani__nazev">Otevřít asistenta</div>
            <div className="hledani__popis">
              Předchozí konverzace a návrhy čekající na potvrzení
            </div>
          </div>
        </div>
      );
    }
    if (volba.druh === 'pokracovat') {
      return (
        <div
          key={volba.klic}
          id={idPolozky(i)}
          role="option"
          aria-selected={aktivni}
          className={trida}
          data-testid="hledani-pokracovat"
          onMouseDown={mouseDownBezFokusu}
          onMouseEnter={() => setZvyraznena(i)}
          onClick={() => provest(volba)}
        >
          <span className="hledani__znacka" aria-hidden="true">
            <ZnackaAsistenta velikost={16} />
          </span>
          <div className="hledani__text">
            <div className="hledani__nazev hledani__nazev--dotaz">
              Pokračovat v poslední konverzaci: {volba.dotaz}
            </div>
            <div className="hledani__popis">
              Dotaz se připojí k předchozí konverzaci s asistentem. Klávesy Ctrl+Shift+Enter.
            </div>
          </div>
          <span className="hledani__sipka" aria-hidden="true">
            ↵
          </span>
        </div>
      );
    }
    if (volba.druh !== 'zeptat') return null;
    return (
      <div
        key={volba.klic}
        id={idPolozky(i)}
        role="option"
        aria-selected={aktivni}
        className={trida}
        data-testid={volba.priklad ? 'hledani-priklad' : 'hledani-zeptat'}
        onMouseDown={mouseDownBezFokusu}
        onMouseEnter={() => setZvyraznena(i)}
        onClick={() => provest(volba)}
      >
        <span className="hledani__znacka" aria-hidden="true">
          <ZnackaAsistenta velikost={16} />
        </span>
        <div className="hledani__text">
          <div className="hledani__nazev hledani__nazev--dotaz">
            {volba.priklad ? volba.dotaz : `Zeptat se asistenta: ${volba.dotaz}`}
          </div>
          {!volba.priklad && (
            <div className="hledani__popis">
              Dohledá a spočítá z dat, která smíte vidět. Nic nezapíše bez vašeho potvrzení.
            </div>
          )}
        </div>
        <span className="hledani__sipka" aria-hidden="true">
          {volba.priklad ? '→' : '↵'}
        </span>
      </div>
    );
  };

  let index = -1;
  const vysledky = (
    <div id={idPanelu} role="listbox" aria-label="Výsledky hledání" className="hledani__vysledky">
      {diktat && (
        <div className="hledani__diktat" onMouseDown={mouseDownBezFokusu}>
          <NacitaniCasti tiche>
            <TlacitkoDiktatu
              pole={pole}
              hodnota={text}
              naZmenu={(hodnota) => {
                setText(hodnota);
                setOtevreno(true);
              }}
              popis="hledání nebo dotaz pro asistenta"
            />
          </NacitaniCasti>
        </div>
      )}
      {!mozneHledat && (
        <p className="hledani__hlaska">
          {hledaniPovoleno
            ? 'Napište aspoň 2 znaky — číslo zakázky, dokladu nebo lístku, název firmy, IČO, SPZ, kód odpadu, jméno…'
            : 'Napište, na co se chcete zeptat.'}
          {asistent && ' Nebo se zeptejte asistenta celou větou.'}
        </p>
      )}
      {chyba && (
        <p className="hledani__hlaska hledani__hlaska--chyba" role="alert">
          Hledání se nepovedlo. {chyba.duvod ?? chyba.zprava}
        </p>
      )}
      {skupiny.map((s) => (
        <div key={s.druh} className="hledani__skupina" role="group" aria-label={s.nazev}>
          <div className="hledani__nadpis">{s.nazev}</div>
          {s.polozky.map((p) => {
            index += 1;
            const i = index;
            return (
              <div
                key={`${p.typ}-${p.id}`}
                id={idPolozky(i)}
                role="option"
                aria-selected={i === zvyraznena}
                className={`hledani__polozka${i === zvyraznena ? ' hledani__polozka--aktivni' : ''}`}
                data-testid="hledani-polozka"
                onMouseDown={mouseDownBezFokusu}
                onMouseEnter={() => setZvyraznena(i)}
                onClick={() => otevritPolozku(p)}
              >
                <div className="hledani__text">
                  <div className="hledani__nazev">{p.nazev}</div>
                  {p.popis && <div className="hledani__popis">{p.popis}</div>}
                  {p.uryvek && <div className="hledani__uryvek">{p.uryvek}</div>}
                </div>
                <span className="hledani__sipka" aria-hidden="true">
                  →
                </span>
              </div>
            );
          })}
          {s.dalsi && (
            <div className="hledani__dalsi">
              {s.odkaz_vse ? (
                <button
                  type="button"
                  className="tlacitko tlacitko--textove"
                  onMouseDown={mouseDownBezFokusu}
                  onClick={() => {
                    zavrit();
                    navigate(s.odkaz_vse as string);
                  }}
                >
                  Zobrazit všechny v sekci
                </button>
              ) : (
                <span>Další výsledky — zpřesněte hledání.</span>
              )}
            </div>
          )}
        </div>
      ))}
      {nacitaZaznamy && polozky.length === 0 && <p className="hledani__hlaska">Hledám…</p>}
      {hledat && nacitaDokumenty && (
        <p className="hledani__hlaska">Hledám i v dokumentech podle smyslu…</p>
      )}
      {hledat &&
        !nacitaDokumenty &&
        stavDokumentu &&
        !stavDokumentu.smysl &&
        stavDokumentu.duvod && <p className="hledani__hlaska">{stavDokumentu.duvod}</p>}
      {prazdno && (
        <p className="hledani__hlaska">
          Nic přesného jsem nenašel. Hledám jen v částech, kam máte přístup.
          {asistent && ' Zkuste se zeptat asistenta.'}
        </p>
      )}
      {asistent && (
        <div
          className={`hledani__asistent${mozneHledat ? ' hledani__asistent--patro' : ''}`}
          role="group"
          aria-label="Asistent"
        >
          {!mozneHledat && <div className="hledani__nadpis">Zkuste se zeptat</div>}
          {volby.map((volba, i) => (volba.druh === 'vysledek' ? null : radekAsistenta(volba, i)))}
        </div>
      )}
    </div>
  );
  const panel = uzke ? (
    <div className="hledani__panel" role="dialog" aria-label="Hledání" data-testid="hledani-panel">
      <div className="hledani__radek-pole">
        {poleHledani()}
        <button
          type="button"
          className="hledani__zavrit"
          aria-label="Zavřít hledání"
          onClick={zavrit}
        >
          <Ikona id="x" velikost={18} />
        </button>
      </div>
      {vysledky}
    </div>
  ) : (
    <div className="hledani__panel" data-testid="hledani-panel">
      {vysledky}
    </div>
  );

  function poleHledani() {
    return (
      <input
        ref={pole}
        type="search"
        className="hlavicka__hledani-pole hledani__pole"
        placeholder={asistent ? 'Hledat nebo se zeptat…' : 'Hledat… zakázku, firmu, vozidlo, odpad'}
        aria-label="Hledat v celé aplikaci"
        role="combobox"
        aria-expanded={ukazatPanel}
        aria-controls={idPanelu}
        aria-autocomplete="list"
        aria-activedescendant={zvyraznena >= 0 ? idPolozky(zvyraznena) : undefined}
        aria-keyshortcuts={asistent ? `${ZKRATKA_HLEDANI} Control+K` : ZKRATKA_HLEDANI}
        title={
          asistent
            ? 'Hledat v celé aplikaci nebo se zeptat asistenta (klávesa / nebo Ctrl+K)'
            : 'Hledat v celé aplikaci (klávesa /)'
        }
        value={text}
        data-testid="hledani-pole"
        onChange={(e) => {
          setText(e.target.value);
          setOtevreno(true);
          setCekaNaEnter(false);
        }}
        onFocus={() => {
          if (!potlacitOtevreni.current) setOtevreno(true);
        }}
        onBlur={(e) => {
          // Klepnutí na řádek nebo na Diktovat v panelu pole neopustí (mousedown se ruší); jiný cíl panel zavře.
          if (!uzke && !koren.current?.contains(e.relatedTarget as Node | null)) zavrit();
        }}
        onKeyDown={naKlavesuPole}
      />
    );
  }

  return (
    <div className="hledani" data-screen-label="Vyhledávání" ref={koren}>
      {uzke ? (
        <button
          ref={tlacitko}
          type="button"
          className="hledani__lupa"
          aria-label="Hledat v celé aplikaci"
          aria-expanded={otevreno}
          aria-haspopup="dialog"
          title={asistent ? 'Hledat nebo se zeptat asistenta' : 'Hledat v celé aplikaci'}
          data-testid="hledani-lupa"
          onClick={() => setOtevreno((o) => !o)}
        >
          <Ikona id="search" velikost={18} />
        </button>
      ) : (
        <div className="hlavicka__hledani">{poleHledani()}</div>
      )}
      {ukazatPanel && (
        <>
          <button
            type="button"
            className="prekryv hledani__podklad"
            aria-label="Zavřít hledání"
            tabIndex={-1}
            onMouseDown={mouseDownBezFokusu}
            onClick={zavrit}
          />
          {panel}
        </>
      )}
    </div>
  );
}
